/**
 * DeudaPage — Fase 4 Combustible.
 * Muestra la deuda vigente con cada estación de servicio y permite
 * liquidar en lote (marcar múltiples cargas como pagadas de una).
 * Uso típico: el contador recibe factura mensual de Terpel con N cargas
 * y las marca todas juntas al pagar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Checkbox, IconButton, Tooltip, Snackbar, Alert,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import PaidIcon from '@mui/icons-material/Paid';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function DeudaPage() {
  const empresaId = getEmpresaId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liquidarDialog, setLiquidarDialog] = useState({ open: false, estacion: null });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/combustible/deuda-por-estacion?empresa_id=${empresaId}`,
        { headers: getAuthHeaders() });
      if (r.ok) setData(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box>
      {/* KPIs globales */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, borderLeft: '4px solid #dc2626' }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Deuda total a estaciones
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#dc2626' }}>
              {fmtMoney(data?.total_general_pendiente || 0)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Paper sx={{ p: 2, borderLeft: '4px solid #f59e0b' }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Cargas a crédito
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#0f172a' }}>
              {data?.total_cargas || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Paper sx={{ p: 2, borderLeft: '4px solid #7c3aed' }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Estaciones con deuda
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#0f172a' }}>
              {data?.num_estaciones || 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', flex: 1 }}>
          Cuentas por pagar · una por estación
        </Typography>
        <Tooltip title="Refrescar"><IconButton onClick={cargar}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: '#dc2626' }} />
        </Box>
      ) : (!data?.estaciones || data.estaciones.length === 0) ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <PaidIcon sx={{ fontSize: 56, color: '#16a34a', mb: 1 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            ¡Sin deudas pendientes!
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Todas las cargas están marcadas como pagadas.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {data.estaciones.map(est => (
            <Grid item xs={12} md={6} key={est.estacion_id || 'sin-estacion'}>
              <Paper sx={{ p: 2, height: '100%', borderTop: '3px solid #dc2626' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <StoreIcon sx={{ color: '#dc2626', fontSize: 22 }} />
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', flex: 1 }}>
                    {est.estacion_nombre}
                  </Typography>
                  {est.dias_antiguedad > 45 && (
                    <Tooltip title={`Carga más antigua: ${est.dias_antiguedad} días`}>
                      <WarningAmberIcon sx={{ color: '#dc2626' }} />
                    </Tooltip>
                  )}
                </Box>

                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                      Total pendiente
                    </Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>
                      {fmtMoney(est.total_pendiente)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                      Cargas · Galones
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                      {est.num_cargas} · {Number(est.total_galones).toLocaleString('es-CO', { maximumFractionDigits: 1 })} gal
                    </Typography>
                  </Grid>
                </Grid>

                <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1 }}>
                  Desde {fmtDate(est.fecha_mas_antigua)} · última {fmtDate(est.fecha_mas_reciente)}
                </Typography>

                <Button fullWidth variant="contained" startIcon={<PaidIcon />}
                  onClick={() => setLiquidarDialog({ open: true, estacion: est })}
                  sx={{
                    textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.35)',
                  }}
                >
                  Liquidar factura
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Diálogo liquidar factura */}
      <LiquidarFacturaDialog
        open={liquidarDialog.open}
        estacion={liquidarDialog.estacion}
        empresaId={empresaId}
        onClose={() => setLiquidarDialog({ open: false, estacion: null })}
        onSuccess={(msg) => {
          setLiquidarDialog({ open: false, estacion: null });
          setSnack({ open: true, msg, severity: 'success' });
          cargar();
        }}
        onError={(msg) => setSnack({ open: true, msg, severity: 'error' })}
      />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}


function LiquidarFacturaDialog({ open, estacion, empresaId, onClose, onSuccess, onError }) {
  const [seleccionadas, setSeleccionadas] = useState(new Set());
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().slice(0, 10));
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && estacion) {
      // Por default, seleccionar TODAS las cargas de la estación
      setSeleccionadas(new Set(estacion.cargas.map(c => c.id)));
      setFechaPago(new Date().toISOString().slice(0, 10));
      setNotas('');
    }
  }, [open, estacion]);

  if (!estacion) return null;

  const toggle = (id) => {
    setSeleccionadas(prev => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });
  };

  const toggleTodos = () => {
    if (seleccionadas.size === estacion.cargas.length) {
      setSeleccionadas(new Set());
    } else {
      setSeleccionadas(new Set(estacion.cargas.map(c => c.id)));
    }
  };

  const totalSeleccionado = estacion.cargas
    .filter(c => seleccionadas.has(c.id))
    .reduce((a, c) => a + Number(c.total_pagado || 0), 0);

  const guardar = async () => {
    if (seleccionadas.size === 0 || saving) return;
    setSaving(true);
    try {
      const body = {
        carga_ids: Array.from(seleccionadas),
        pagada_at: fechaPago ? new Date(fechaPago + 'T12:00:00').toISOString() : null,
        notas: notas.trim() || null,
      };
      const r = await fetch(
        `${API_BASE}/turismo/combustible/cargas/marcar-pagadas-bulk?empresa_id=${empresaId}`,
        { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) }
      );
      const d = await r.json();
      if (!r.ok) {
        onError?.(d.detail || 'Error al liquidar');
        return;
      }
      onSuccess?.(`${d.marcadas_como_pagadas} cargas liquidadas · $${Number(d.monto_total_liquidado).toLocaleString('es-CO')}`);
    } catch (e) {
      onError?.('Error de red');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <PaidIcon sx={{ color: '#16a34a' }} />
        Liquidar factura · {estacion.estacion_nombre}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Marca las cargas incluidas en la factura mensual que estás pagando.
          Todas las seleccionadas pasarán a estado <strong>Pagada</strong> en una sola transacción.
        </Alert>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date" label="Fecha del pago *"
              InputLabelProps={{ shrink: true }}
              value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Notas (opcional)"
              value={notas} onChange={(e) => setNotas(e.target.value)}
              placeholder="Ej: Factura #TRP-8792"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', flex: 1 }}>
            {seleccionadas.size} de {estacion.cargas.length} cargas seleccionadas · Total: {fmtMoney(totalSeleccionado)}
          </Typography>
          <Button size="small" onClick={toggleTodos} sx={{ textTransform: 'none' }}>
            {seleccionadas.size === estacion.cargas.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={seleccionadas.size > 0 && seleccionadas.size < estacion.cargas.length}
                    checked={seleccionadas.size === estacion.cargas.length}
                    onChange={toggleTodos}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Embarcación</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Galones</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ticket</TableCell>
                <TableCell align="center">Factura</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estacion.cargas.map(c => (
                <TableRow key={c.id} hover selected={seleccionadas.has(c.id)}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => toggle(c.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={seleccionadas.has(c.id)} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(c.fecha_carga)}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{c.embarcacion_nombre || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>
                    {Number(c.galones).toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>
                    {fmtMoney(c.total_pagado)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, color: '#64748b' }}>
                    {c.referencia_ticket || '—'}
                  </TableCell>
                  <TableCell align="center">
                    {c.factura_url && (
                      <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); window.open(c.factura_url, '_blank'); }}
                      >
                        <ReceiptLongIcon fontSize="small" sx={{ color: '#dc2626' }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={guardar}
          disabled={seleccionadas.size === 0 || saving}
          startIcon={<PaidIcon />}
          sx={{
            textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
          }}
        >
          {saving ? 'Liquidando…' : `Marcar como pagadas (${seleccionadas.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
